import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  CLOSING_TASK_STATUSES,
  TASK_STATUSES,
  canCloseTask,
  canProject,
  type Task,
  type TaskStatus,
} from '@allemnii-pulse/shared';
import { useCollection } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { useAuth } from '../auth/AuthProvider.js';
import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select.js';
import { SourceBadge } from '../../components/ui/SourceBadge.js';
import { UserName } from '../../components/UserName.js';
import { AddTaskDialog } from './AddTaskDialog.js';

const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  blocked: 'Blocked',
  testing: 'Testing',
  in_review: 'In Review',
  done: 'Done',
  canceled: 'Canceled',
};

export function TaskBoardPage() {
  const { projectId, role } = useProject();
  const { user } = useAuth();
  const { data: tasks } = useCollection<Task>(`projects/${projectId}/tasks`);
  const canEdit = canProject(role, 'task.edit');

  /** The statuses this user may move `t` to — done/canceled only if they can close it. */
  function statusOptionsFor(t: Task) {
    const canClose = canCloseTask(role, user?.uid ?? '', t);
    return TASK_STATUSES.filter(
      (s) =>
        canClose || s === t.status || !(CLOSING_TASK_STATUSES as readonly string[]).includes(s),
    );
  }
  const [showAddDialog, setShowAddDialog] = useState(false);

  async function moveTask(taskId: string, status: TaskStatus) {
    await api.patch(`/projects/${projectId}/tasks/${taskId}`, { status });
  }

  return (
    <div className="flex h-full flex-col gap-3 p-4 sm:p-6">
      <div className="flex items-center">
        {canProject(role, 'task.create') && (
          <Button variant="default" className="ml-auto" onClick={() => setShowAddDialog(true)}>
            Add task
          </Button>
        )}
      </div>
      {/* On phones the board bleeds to the screen edge and snaps column by column, with the next
          column peeking in so it's clear there's more to swipe to. */}
      <div className="-mx-4 flex flex-1 snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 sm:mx-0 sm:scroll-px-0 sm:px-0 md:snap-none">
        {TASK_STATUSES.map((status) => {
          const columnTasks = tasks.filter((t) => t.status === status);
          return (
            <div key={status} className="flex w-[85vw] max-w-80 flex-shrink-0 snap-start flex-col gap-2 sm:w-64">
              <div className="flex items-center gap-2 px-1">
                <span className="text-xs font-semibold">{STATUS_LABEL[status]}</span>
                <span className="rounded-full bg-surface-2 px-1.5 text-[10px] font-semibold text-muted">
                  {columnTasks.length}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <AnimatePresence initial={false}>
                  {columnTasks.map((t) => (
                    <motion.div
                      key={t.id}
                      layoutId={t.id}
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.18, ease: 'easeOut' }}
                      whileHover={{ y: -2 }}
                      className={`flex flex-col gap-1.5 rounded-lg border bg-surface p-2.5 shadow-sm ${
                        status === 'blocked' ? 'border-danger/40' : 'border-border'
                      }`}
                    >
                      <Link
                        to={`/projects/${projectId}/tasks/${t.id}`}
                        className="break-words text-sm font-medium hover:text-accent md:text-xs"
                      >
                        {t.title}
                      </Link>
                      <div className="flex items-center gap-1.5">
                        {t.assigneeUid && (
                          <UserName uid={t.assigneeUid} avatar className="text-[10px] text-muted" />
                        )}
                        {t.source === 'manual' && <SourceBadge source={t.source} />}
                      </div>
                      <Select
                        value={t.status}
                        disabled={!canEdit}
                        onValueChange={(v) => void moveTask(t.id, v as TaskStatus)}
                      >
                        <SelectTrigger className="h-10 text-xs md:h-6 md:text-[10px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptionsFor(t).map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
      </div>
      <AddTaskDialog
        projectId={projectId}
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />
    </div>
  );
}
